/**
 * 用户类型策略处理器
 * 
 * @description 基于策略模式实现不同用户类型的队列处理逻辑
 * @author Frontend Architect
 * @version 2.0.0
 */

import { 
  UserType, 
  QueueStage, 
  type QueueTask, 
  type UserTypeStrategy 
} from '@/types/taskQueue';
import { randomBetween } from '../constants';
import { useTaskQueueStore } from '@/store/taskQueue';

/** 抽象用户策略基类 */
export abstract class BaseUserStrategy {
  protected config: UserTypeStrategy;

  constructor(config: UserTypeStrategy) {
    this.config = config;
  }

  /** 获取用户类型 */
  getUserType(): UserType {
    return this.config.userType;
  }

  /** 获取阶段配置 */
  getStageConfig(stage: QueueStage) {
    return this.config.stages[stage];
  }

  /** 检查是否超过总等待时间限制 */
  isExceedMaxTotalWaitTime(totalWaitTime: number): boolean {
    return totalWaitTime >= this.config.maxTotalWaitTime;
  }

  /** 抽象方法：初始化队列数据 */
  abstract initializeQueueData(task: QueueTask): Partial<QueueTask>;

  /** 抽象方法：处理排队逻辑 */
  abstract processQueueing(task: QueueTask, updateCallback?: (updates: Partial<QueueTask>) => void): Partial<QueueTask>;

  /** 抽象方法：获取下一阶段 */
  abstract getNextStage(currentStage: QueueStage): QueueStage | null;

  /** 计算预计等待时间 */
  calculateEstimatedWaitTime(queueNum: number): number {
    if (queueNum <= 0) return 0;
    
    // 简化计算：当前排队数 * 5秒
    return queueNum * 5;
  }

  /** 格式化消息模板 */
  protected formatMessage(template: string, data: Record<string, any>): string {
    return template.replace(/\{(\w+)\}/g, (match, key) => {
      return data[key]?.toString() || match;
    });
  }
}

/** 免费用户策略 */
export class FreeUserStrategy extends BaseUserStrategy {
  // 使用Map来管理每个任务的定时器，避免多任务间的冲突
  private queueReductionTimers: Map<string, NodeJS.Timeout> = new Map();

  /** 重写预计等待时间计算方法 - 简化为排队数×5秒 */
  calculateEstimatedWaitTime(queueNum: number): number {
    if (queueNum <= 0) return 0;
    
    // 简化计算：当前排队数 * 5秒
    return queueNum * 5;
  }

  initializeQueueData(_task: QueueTask): Partial<QueueTask> {
    const stageConfig = this.getStageConfig(QueueStage.QUEUING);
    const queueRange = stageConfig.config?.initialQueueRange || [75, 250];
    const queueNum = randomBetween(queueRange[0], queueRange[1]);
    
    return {
      queueNum,
      queueTotalNum: queueNum,
      estimatedWaitTime: this.calculateEstimatedWaitTime(queueNum),
      message: this.formatMessage(
        stageConfig.config?.messageTemplate || '当前排队 {queueNum} 名',
        { queueNum }
      )
    };
  }

  processQueueing(task: QueueTask, updateCallback?: (updates: Partial<QueueTask>) => void): Partial<QueueTask> {
    // 如果已经有定时器在运行，说明可能被重复调用了
    if (this.queueReductionTimers.has(task.clientId)) {
      return {};
    }

    // 开始排队减少逻辑
    if (updateCallback) {
      this.startQueueReduction(task, updateCallback);
    }

    return {};
  }

  private startQueueReduction(task: QueueTask, updateCallback: (updates: Partial<QueueTask>) => void): void {
    // 先清理可能存在的旧定时器
    this.clearQueueTimer(task.clientId);
    
    const stageConfig = this.getStageConfig(QueueStage.QUEUING);
    const interval = stageConfig.config?.reductionInterval || 5000;

    const taskQueueStore = useTaskQueueStore();

    const reductionTimer = setInterval(() => {
      // 获取最新任务状态
      const latestTask = taskQueueStore.getTaskStatus(task.clientId);
      if (!latestTask || latestTask.stage !== QueueStage.QUEUING || latestTask.queueNum <= 0) {
        clearInterval(reductionTimer);
        this.queueReductionTimers.delete(task.clientId);
        return;
      }

      // 计算减少数量 - 使用最新的排队数据
      const reductionPercentageRange = stageConfig.config?.reductionPercentageRange || [3, 6];
      const minReduction = stageConfig.config?.minReduction || 5;
      
      const reductionPercentage = randomBetween(
        reductionPercentageRange[0], 
        reductionPercentageRange[1]
      );
      
      const reductionAmount = Math.max(
        Math.floor(latestTask.queueNum * reductionPercentage / 100),
        minReduction
      );

      const newQueueNum = Math.max(0, latestTask.queueNum - reductionAmount);
      
      // 检查是否需要停止倒计时：当排队数<5时停止自动减少，等待外部生图开始
      if (newQueueNum < 5) {
        clearInterval(reductionTimer);
        this.queueReductionTimers.delete(task.clientId);
        
        // 更新到当前状态后停止，不再自动更新
        updateCallback({
          queueNum: newQueueNum,
          estimatedWaitTime: this.calculateEstimatedWaitTime(newQueueNum),
          message: this.formatMessage(
            stageConfig.config?.messageTemplate || '当前排队 {queueNum} 名',
            { queueNum: newQueueNum }
          )
        });
        
        return;
      }
      
      // 通过回调更新任务状态，确保响应式更新
      updateCallback({
        queueNum: newQueueNum,
        estimatedWaitTime: this.calculateEstimatedWaitTime(newQueueNum),
        message: this.formatMessage(
          stageConfig.config?.messageTemplate || '当前排队 {queueNum} 名',
          { queueNum: newQueueNum }
        )
      });

      // 如果排队结束，进入下一阶段
      if (newQueueNum === 0) {
        clearInterval(reductionTimer);
        this.queueReductionTimers.delete(task.clientId);
      }
    }, interval);

    // 存储定时器
    this.queueReductionTimers.set(task.clientId, reductionTimer);
  }

  private clearQueueTimer(clientId: string): void {
    const timer = this.queueReductionTimers.get(clientId);
    if (timer) {
      clearInterval(timer);
      this.queueReductionTimers.delete(clientId);
    }
  }

  private clearAllQueueTimers(): void {
    this.queueReductionTimers.forEach((timer, clientId) => {
      clearInterval(timer);
    });
    this.queueReductionTimers.clear();
  }

  getNextStage(currentStage: QueueStage): QueueStage | null {
    const stageFlow = [
      QueueStage.INITIALIZING,
      QueueStage.QUEUING,
      QueueStage.GENERATING,
      QueueStage.COMPLETED
    ];

    const currentIndex = stageFlow.indexOf(currentStage);
    return currentIndex >= 0 && currentIndex < stageFlow.length - 1 
      ? stageFlow[currentIndex + 1] 
      : null;
  }

  /** 清理资源 */
  destroy(): void {
    this.clearAllQueueTimers();
  }
}

/** 付费用户策略（VIP/SVIP共同基类） */
export abstract class PaidUserStrategy extends BaseUserStrategy {
  // 使用Map来管理每个任务的定时器，避免多任务间的冲突
  private queueTimers: Map<string, NodeJS.Timeout> = new Map();
  // 存储每个任务的实际等待时间
  private actualWaitTimes: Map<string, number> = new Map();

  initializeQueueData(task: QueueTask): Partial<QueueTask> {
    const stageConfig = this.getStageConfig(QueueStage.QUEUING);
    
    // 假排队总数显示为随机值75-250
    const fakeQueueRange = stageConfig.config?.fakeQueueRange || [75, 250];
    const fakeQueueTotalNum = randomBetween(fakeQueueRange[0], fakeQueueRange[1]);
    
    // 实际当前排队数：VIP(3-6)，SVIP(1-3)
    const actualQueueRange = stageConfig.config?.actualQueueRange || [1, 5];
    const actualCurrentQueueNum = randomBetween(actualQueueRange[0], actualQueueRange[1]);
    
    // 实际等待时间：VIP(3-6秒)，SVIP(1-4秒)
    const actualWaitTimeRange = stageConfig.config?.actualWaitTimeRange || [1, 5];
    const actualWaitTime = randomBetween(actualWaitTimeRange[0], actualWaitTimeRange[1]);
    
    // 存储实际等待时间
    this.actualWaitTimes.set(task.clientId, actualWaitTime);
    
    return {
      queueNum: actualCurrentQueueNum, // 显示实际当前排队数
      queueTotalNum: fakeQueueTotalNum, // 显示假排队总数
      estimatedWaitTime: actualWaitTime,
      message: this.formatMessage(
        stageConfig.config?.messageTemplate || '当前排队 {queueNum} 名',
        { queueNum: actualCurrentQueueNum }
      )
    };
  }

  processQueueing(task: QueueTask, updateCallback?: (updates: Partial<QueueTask>) => void): Partial<QueueTask> {
    // 如果已经有定时器在运行，说明可能被重复调用了
    if (this.queueTimers.has(task.clientId)) {
      return {};
    }
    
    if (!updateCallback) {
      return {};
    }

    // 获取实际等待时间（从存储的Map中获取）
    const totalWaitTime = this.actualWaitTimes.get(task.clientId);
    if (!totalWaitTime) {
      const stageConfig = this.getStageConfig(QueueStage.QUEUING);
      const actualWaitTimeRange = stageConfig.config?.actualWaitTimeRange || [1, 5];
      const defaultWaitTime = randomBetween(actualWaitTimeRange[0], actualWaitTimeRange[1]);
      this.actualWaitTimes.set(task.clientId, defaultWaitTime);
    }

    // 开始排队减少动画
    this.startPaidQueueReduction(task, updateCallback, totalWaitTime || 3);

    return {};
  }

  /** 付费用户排队减少动画 */
  private startPaidQueueReduction(task: QueueTask, updateCallback: (updates: Partial<QueueTask>) => void, totalWaitTime: number): void {
    // 先清理可能存在的旧定时器
    this.clearQueueTimer(task.clientId);
    
    const taskQueueStore = useTaskQueueStore();
    const currentTask = taskQueueStore.getTaskStatus(task.clientId);
    
    if (!currentTask) {
      return;
    }

    const initialQueueNum = currentTask.queueNum; // 使用实际的当前排队数
    const totalSteps = initialQueueNum; // 每次减少1人，总步数等于初始排队数
    const stepInterval = (totalWaitTime * 1000) / totalSteps; // 平均每步间隔时间
    let currentStep = 0;

    const reductionTimer = setInterval(() => {
      // 获取最新任务状态
      const latestTask = taskQueueStore.getTaskStatus(task.clientId);
      if (!latestTask || latestTask.stage !== QueueStage.QUEUING) {
        clearInterval(reductionTimer);
        this.queueTimers.delete(task.clientId);
        return;
      }

      currentStep++;
      
      // 计算当前排队数：从初始值逐步减少到0
      const newQueueNum = Math.max(0, initialQueueNum - currentStep);

      // 更新排队数据
      updateCallback({
        queueNum: newQueueNum,
        estimatedWaitTime: Math.max(0, Math.ceil((totalSteps - currentStep) * stepInterval / 1000)), // 剩余时间
        message: this.formatMessage(
          this.getStageConfig(QueueStage.QUEUING).config?.messageTemplate || '当前排队 {queueNum} 名',
          { queueNum: newQueueNum }
        )
      });

      // 排队完成
      if (newQueueNum <= 0) {
        clearInterval(reductionTimer);
        this.queueTimers.delete(task.clientId);
        
        // 确保最终为0
        updateCallback({
          queueNum: 0,
          estimatedWaitTime: 0,
          message: '即将开始生成...'
        });
      }
    }, stepInterval);

    // 存储定时器引用以便清理
    this.queueTimers.set(task.clientId, reductionTimer);
  }

  /** 清理定时器 */
  private clearQueueTimer(clientId: string): void {
    const timer = this.queueTimers.get(clientId);
    if (timer) {
      clearInterval(timer);
      this.queueTimers.delete(clientId);
    }
    // 同时清理存储的等待时间
    this.actualWaitTimes.delete(clientId);
  }

  /** 清理所有定时器 */
  private clearAllQueueTimers(): void {
    for (const [clientId, timer] of this.queueTimers.entries()) {
      clearInterval(timer);
    }
    this.queueTimers.clear();
    // 清理所有等待时间数据
    this.actualWaitTimes.clear();
  }

  /** 清理资源 */
  destroy(): void {
    this.clearAllQueueTimers();
  }

  getNextStage(currentStage: QueueStage): QueueStage | null {
    const stageFlow = [
      QueueStage.INITIALIZING,
      QueueStage.QUEUING,
      QueueStage.MODEL_LOADING,
      QueueStage.GENERATING,
      QueueStage.COMPLETED
    ];

    const currentIndex = stageFlow.indexOf(currentStage);
    return currentIndex >= 0 && currentIndex < stageFlow.length - 1 
      ? stageFlow[currentIndex + 1] 
      : null;
  }
}

/** 标准版会员策略 */
export class VipUserStrategy extends PaidUserStrategy {
  constructor(config: UserTypeStrategy) {
    super(config);
  }
}

/** 专业版会员策略 */
export class SvipUserStrategy extends PaidUserStrategy {
  constructor(config: UserTypeStrategy) {
    super(config);
  }
}

/** 策略构造函数类型 */
type StrategyConstructor = new (config: UserTypeStrategy) => BaseUserStrategy;

/** 策略工厂 */
export class UserStrategyFactory {
  private static strategies = new Map<UserType, StrategyConstructor>();

  static {
    // 注册策略类
    this.strategies.set(UserType.FREE, FreeUserStrategy);
    this.strategies.set(UserType.VIP, VipUserStrategy);
    this.strategies.set(UserType.SVIP, SvipUserStrategy);
  }

  /** 创建用户策略实例 */
  static createStrategy(userType: UserType, config: UserTypeStrategy): BaseUserStrategy {

    const StrategyClass = this.strategies.get(userType);
    
    if (!StrategyClass) {
      throw new Error(`Unsupported user type: ${userType}`);
    }

    return new StrategyClass(config);
  }

  /** 注册新的策略类 */
  static registerStrategy(userType: UserType, strategyClass: StrategyConstructor): void {
    this.strategies.set(userType, strategyClass);
  }
} 